import React, { useState, useEffect } from 'react';

import { observer, useLocalObservable } from 'mobx-react-lite';
import { SingleDatePicker } from 'react-dates';

import Container from '@vromo/ui/components/Container';
import { useStore } from '@vromo/mobx-react/mobx-react';
import { RootStore } from '../stores/RootStore';
import moment, { Moment } from 'moment';
import { EntityList, Group } from '@vromo/models';
import { Job } from '@vromo/models';
import { update } from 'serializr';
import { startOfDay, endOfDay, startOfToday, addMinutes, format } from 'date-fns';
import { action } from 'mobx';
import groupBy from 'lodash/groupBy';
import Button from '@vromo/ui/components/Button';
import theme from '@vromo/ui/components/theme';

import Select, { ActionMeta, OptionsType } from 'react-select';

import Tracking from '@vromo/analytics';

import { AutoSizer } from 'react-virtualized';

import PlanMap from './PlanMap';
import sortBy from 'lodash/sortBy';
import findLast from 'lodash/findLast';
import Table from '@vromo/ui/components/Table';
import Cell from './Cell';
import DispatchModal from './DispatchModal';
import { last } from 'lodash';

export default observer(function Plan() {
    const { groupStore, vehicleStore, authStore } = useStore<RootStore>('rootStore');
    const { graph, role } = authStore;

    const [dispatchOpen, setDispatchOpen] = useState(false);
    const [groups, setGroups] = useState<Group[]>([]);
    const [vehicleGroups, setVehicleGroups] = useState<Group[]>([]);
    const [date, setDate] = useState<Date>(new Date());
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        store.date = date;
    }, [date]);

    const unselectedSuccessorsIn = (items: Group[]) => (it: Group): Group[] => {
        return groupStore
            .getChildren(it)
            .filter(({ id: searchedId }) => !items?.map(({ id }) => id).includes(searchedId));
    };

    const unselectedSuccessorsForGroups = unselectedSuccessorsIn(groups);
    const unselectedSuccessorsForVehicleGroup = unselectedSuccessorsIn(vehicleGroups);

    const handleSelectedGroupChange = (
        selected: OptionsType<Group>,
        actionMeta: ActionMeta<Group>
    ) => {
        const group = last(selected)!;
        const newGroups =
            actionMeta.action === 'select-option' ? unselectedSuccessorsForGroups(group!) : [];
        setGroups([...selected, ...newGroups]);
    };

    const handleSelectedVehicleGroupChange = (
        selected: OptionsType<Group>,
        actionMeta: ActionMeta<Group>
    ) => {
        const group = last(selected)!;
        const newGroups =
            actionMeta.action === 'select-option'
                ? unselectedSuccessorsForVehicleGroup(group!)
                : [];
        setVehicleGroups([...selected, ...newGroups]);
    };

    const store = useLocalObservable(() => ({
        date,
        jobs: new EntityList(Job),
        selected: new Set<number>(),
        vehicle: 0,

        get selectedQuarters() {
            const selected = new Set();

            Array.from(store.selected).forEach((it) => {
                const job = store.jobs.find((job) => job.id === it)!;
                selected.add(
                    job.nextDeadline
                        ? Math.trunc(
                              (job.nextDeadline - startOfDay(job.nextDeadline).valueOf()) /
                                  1000 /
                                  60 /
                                  15
                          )
                        : 0
                );
            });

            return selected;
        },

        get today() {
            return this.jobs.filter(
                (it) =>
                    !it.nextDeadline ||
                    (it.nextDeadline > startOfDay(this.date).valueOf() &&
                        it.nextDeadline < endOfDay(this.date).valueOf())
            );
        },

        get quarterly() {
            return groupBy(
                this.today.filter((it) => it.status === 'dispatched' && it.vehicle),
                (it) =>
                    `${it.vehicle}-${
                        it.nextDeadline
                            ? Math.trunc(
                                  (it.nextDeadline - startOfDay(it.nextDeadline).valueOf()) /
                                      1000 /
                                      60 /
                                      15
                              )
                            : 0
                    }`
            );
        },

        get groupSelectorOptions() {
            return sortBy(groupStore.groups.items, [
                (it) =>
                    this.today.filter((job) => job.group === it.id && job.status === 'open')
                        .length * -1,
                // it => store.today.filter(job => job.group === it.id).length * -1,
                (it) => it.name
            ]);
        }
    }));

    useEffect(() => {
        if (!role.id) return;
        graph.root
            .graph()
            .roles(role.id)
            .jobs()
            .get({
                _all_status__in: 'open,dispatched',
                // _all_group__in: groups.map(({ id }) => id).join(','),
                _size__lte: 2500
            })
            .then(({ items }) => {
                update(store.jobs, { items });
            });
    }, []);

    useEffect(() => {
        Tracking.track('Planning View Opened');

        // return when(
        //     () => groupStore.groups.length !== 0,
        //     () => {
        //         const firstGroup = groupStore.groups.first()!;
        //         if (!groups.length) {
        //             setGroups([firstGroup]);
        //             setVehicleGroups([firstGroup]);
        //         }
        //     }
        // );
    }, []);

    return (
        <div>
            <div style={{ padding: 8 }}>
                <div>
                    <Container
                        style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <SingleDatePicker
                            date={moment(date)}
                            onDateChange={(date: Moment | null) => date && setDate(date?.toDate())}
                            focused={focused}
                            onFocusChange={({ focused }: { focused: boolean | null }) =>
                                setFocused(focused || false)
                            }
                            displayFormat="DD MMM YYYY"
                        />

                        <Select
                            value={groups}
                            options={store.groupSelectorOptions}
                            onChange={handleSelectedGroupChange}
                            getOptionLabel={(it) =>
                                `${it.name} [${
                                    store.today.filter((job) => job.group === it.id).length
                                }] (${
                                    store.today.filter(
                                        (job) => job.group === it.id && job.status === 'open'
                                    ).length
                                })`
                            }
                            getOptionValue={(it) => it.id?.toString() || ''}
                            isMulti
                            styles={{
                                container: (provided) => ({ ...provided, flex: 1, marginLeft: 8 }),
                                valueContainer: (base) => ({
                                    ...base,
                                    overflow: 'auto',
                                    'maxHeight': '100px'
                                })
                            }}
                        />
                    </Container>
                </div>

                <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
                    <div
                        style={{
                            backgroundColor: 'green',
                            margin: 8,
                            flex: 1
                        }}>
                        <AutoSizer>
                            {({ width, height }) => (
                                <PlanMap
                                    width={width}
                                    height={height}
                                    store={store}
                                    selected={store.selected}
                                />
                            )}
                        </AutoSizer>
                    </div>

                    <Container style={{ flex: 1 }}>
                        <Button
                            disabled={!store.selected.size}
                            onClick={() => setDispatchOpen(true)}>
                            Dispatch
                        </Button>
                        {dispatchOpen ? (
                            <DispatchModal
                                selected={store.selected}
                                jobs={store.jobs}
                                vehicles={vehicleStore.vehicles.filter((it) =>
                                    groups.map((it) => it!.id).includes(it.group)
                                )}
                                isOpen={true}
                                close={action((dispatched) => {
                                    if (dispatched) {
                                        store.selected.clear();
                                    }

                                    setDispatchOpen(false);
                                })}
                            />
                        ) : null}
                        <div
                            style={{
                                height: 500,
                                maxWidth: 800,
                                flex: 1,
                                overflow: 'auto',
                                whiteSpace: 'nowrap'
                            }}>
                            <Table>
                                <thead>
                                    <tr style={{ textAlign: 'left' }}>
                                        <th />
                                        <th>Name</th>
                                        <th>First Deadline</th>
                                        <th>Last Deadline</th>
                                        <th>First Stop</th>
                                        <th>Last Stop</th>
                                        <th>Cost</th>
                                        <th>Contact Name</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {store.today
                                        .filter(
                                            (it) =>
                                                it.status === 'open' &&
                                                groups.map((it) => it!.id).includes(it.group)
                                        )
                                        .map((it) => {
                                            const jobDeadlines = it.tasks.map(
                                                (t) => t?.timeframe?.by
                                            );

                                            const firstDeadline = jobDeadlines.find(
                                                (d) => d !== null
                                            );
                                            const lastDeadline = findLast(
                                                jobDeadlines,
                                                (d) => d !== null
                                            );
                                            return (
                                                <tr key={it.id}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={store.selected.has(it.id!)}
                                                            onChange={action((e) => {
                                                                if (e.target.checked) {
                                                                    store.selected.add(it.id!);
                                                                } else {
                                                                    store.selected.delete(it.id!);
                                                                }
                                                            })}
                                                        />
                                                    </td>
                                                    <td>{it.name}</td>
                                                    <td>
                                                        {firstDeadline
                                                            ? format(
                                                                  firstDeadline,
                                                                  'hh:mma DD MMM YYYY'
                                                              )
                                                            : 'ASAP'}
                                                    </td>
                                                    <td>
                                                        {lastDeadline
                                                            ? format(
                                                                  lastDeadline,
                                                                  'hh:mma DD MMM YYYY'
                                                              )
                                                            : 'ASAP'}
                                                    </td>

                                                    <td>
                                                        {it.tasks.first()
                                                            ? it.tasks.first()!.zone!.address
                                                            : ''}
                                                    </td>

                                                    <td>
                                                        {it.tasks.last() && it.tasks.length > 1
                                                            ? it.tasks.last()!.zone!.address
                                                            : ''}
                                                    </td>

                                                    <td>
                                                        {it.attr &&
                                                        it.attr.items.find((it) =>
                                                            it.key!.match(/order cost/i)
                                                        )
                                                            ? it.attr.items.find((it) =>
                                                                  it.key!.match(/order cost/i)
                                                              )!.value
                                                            : ''}
                                                    </td>

                                                    <td>
                                                        {it.contact && it.contact.name
                                                            ? it.contact.name
                                                            : ''}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    {store.today
                                        .filter(
                                            (it) =>
                                                (it.status === 'dispatched' ||
                                                    it.status === 'underway') &&
                                                groups.map((it) => it!.id).includes(it.group)
                                        )
                                        .map((it) => {
                                            const jobDeadlines = it.tasks.map(
                                                (t) => t?.timeframe?.by
                                            );
                                            const firstDeadline = jobDeadlines.find(
                                                (d) => d !== null
                                            );
                                            const lastDeadline = findLast(
                                                jobDeadlines,
                                                (d) => d !== null
                                            );
                                            return (
                                                <tr key={it.id} style={{ opacity: 0.7 }}>
                                                    <td></td>
                                                    <td>{it.name}</td>
                                                    <td>
                                                        {firstDeadline
                                                            ? format(
                                                                  firstDeadline,
                                                                  'hh:mma DD MMM YYYY'
                                                              )
                                                            : 'ASAP'}
                                                    </td>
                                                    <td>
                                                        {lastDeadline
                                                            ? format(
                                                                  lastDeadline,
                                                                  'hh:mma DD MMM YYYY'
                                                              )
                                                            : 'ASAP'}
                                                    </td>
                                                    <td>
                                                        {it.tasks.first()
                                                            ? it.tasks.first()!.zone!.address
                                                            : ''}
                                                    </td>

                                                    <td>
                                                        {it.tasks.last() && it.tasks.length > 1
                                                            ? it.tasks.last()!.zone!.address
                                                            : ''}
                                                    </td>

                                                    <td>
                                                        {it.attr &&
                                                        it.attr.items.find((it) =>
                                                            it.key!.match(/order cost/i)
                                                        )
                                                            ? it.attr.items.find((it) =>
                                                                  it.key!.match(/order cost/i)
                                                              )!.value
                                                            : ''}
                                                    </td>

                                                    <td>
                                                        {it.contact && it.contact.name
                                                            ? it.contact.name
                                                            : ''}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </Table>
                        </div>
                    </Container>
                </div>

                <Container>
                    <Select
                        value={vehicleGroups}
                        options={store.groupSelectorOptions}
                        onChange={handleSelectedVehicleGroupChange}
                        getOptionLabel={(it) => it.name || ''}
                        getOptionValue={(it) => it.id?.toString() || ''}
                        styles={{
                            valueContainer: (base) => ({
                                ...base,
                                overflow: 'auto',
                                'maxHeight': '100px'
                            })
                        }}
                        isMulti
                    />
                    <div
                        style={{
                            width: '100%',
                            overflowX: 'auto',
                            display: 'flex',
                            flexDirection: 'row'
                        }}>
                        <table style={{ flexShrink: 0, paddingTop: 16 }}>
                            <tbody>
                                {sortBy(
                                    vehicleStore.vehicles.filter((it) =>
                                        vehicleGroups.map((it) => it!.id).includes(it.group)
                                    ),
                                    ['displayName']
                                ).map((it) => (
                                    <tr
                                        key={it.id}
                                        style={{
                                            height: 26,
                                            color:
                                                it.id === store.vehicle ? theme.primary : 'inherit',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => (store.vehicle = it.id!)}>
                                        <td>
                                            {it.displayName(
                                                vehicleStore.driverVehicleMap,
                                                groupStore.withId(it.group!)
                                            )}
                                        </td>
                                        <td>{it.driver?.contact?.phone}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{ flex: 1, overflowX: 'auto' }}>
                            <table>
                                <thead>
                                    <tr>
                                        {[...new Array(24 * 2)].map((it, ix) => (
                                            <th
                                                key={ix}
                                                style={{
                                                    fontSize: 10,
                                                    fontWeight: 400,
                                                    textAlign: 'left',
                                                    color: store.selectedQuarters.has(ix)
                                                        ? 'orange'
                                                        : 'inherit'
                                                }}
                                                colSpan={2}>
                                                {format(
                                                    addMinutes(startOfToday(), 30 * ix),
                                                    'HH:mm'
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortBy(
                                        vehicleStore.vehicles.filter((it) =>
                                            vehicleGroups.map((it) => it!.id).includes(it.group)
                                        ),
                                        ['displayName']
                                    ).map((it) => (
                                        <tr key={it.id}>
                                            {[...new Array(24 * 4)].map((_, cell) => (
                                                <td key={cell}>
                                                    <Cell
                                                        highlighted={store.selectedQuarters.has(
                                                            cell
                                                        )}
                                                        jobs={store.quarterly[`${it.id}-${cell}`]}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Container>
            </div>
        </div>
    );
});
